<template>
<!-- TODO:
   - Main Info tab should be red
   - Move the v-form inside dialog v-card-text
   - Fix import name placeholder

   BUSINESS LOGICS:
   - scope cannot be modified anymore if Import Level is selected.
   - purl and dedup columns cannot be modified if scope is 'account'.
   - purl and dedup columns are not needed if generatePURL is false.
-->
<!-- HISTORY:
   05/27/22(B1.4): Added typeCasting switch.
   05/02/22(B1.3): Implemented logout feature + Disabled Notifications and GPG tabs.
   04/14/22(B1.2): Commented Actions header (from Aref side).
   03/17/22(B1.1): Showed New Import btn only if the user has contact-imports-create policy + Changed the icon.
   02/25/22(B1.0): Made Refresh btn hidden until an import is available + Added hide-default-footer prop to v-data-table +
      Fixed the bug that didn't show the 1st item (new import) + Fixed the bug that didn't show imports when account was changed.
   02/17/22(B0.12): Changed 30 sec delay for purlProcessedRecords to 2 and added 20 sec for the processedRecords as well +
      Changed the layout of expanded-item to be 2 columns with Headers to be in one + Added PURLs fields to searchFields and set their isIndexed properties.
   02/15/22(B0.11): Added Tooltip to the processedRecords and purlProcessedRecords columns to show the processing time and speed.
   01/14/22(B0.10): Added purlLastActivityDate to the table.
   01/13/22(B0.9): shouldGeneratePurl & dedupHashMatching were allowed to be set during 1st import creation only.
   01/11/22(B0.8): Added dedupHashMatching to the Main Info tab.
   01/06/22(B0.7): Added PURL Status and PURL Processed Records to headers.
   07/26/21(B0.6): Added Creation Date, Processed Records, Start Date and Last Activity Date to searchFields.
   07/26/21(B0.5): Consumed BtFilterDefiner to provide search option + Added pagination logic +
      Removed aid from all methods params.
   03/24/21(B0.4): Merged Aref's code + Added _id and header + Set icon for Status.
   03/23/21(B0.3): In SaveImport(), only passed existed values instead of all.
   03/19/21(B0.2): Removed Config concept and implemented new rules.
   xx/xx/20(B0.1): 1st version/release.
-->
<v-container fluid class="px-3 py-3">
   <v-card>
      <v-card-title class="pl-2">
         <h1 class="title font-weight-bold grey--text darken-4 pl-2" style="color:#757575 !important">
            <!-- <v-icon class="pr-1">import_contacts</v-icon> -->
            <v-icon class="pr-1">file_download</v-icon>
            <span>Imports</span>
         </h1>
         <div class="flex-grow-1"></div>
         <div>
            <!-- <v-text-field single-line hide-details
               class="pt-0"
               label="Search"
               append-icon="search"
               :disabled="!imports || imports.length < 2"
               v-model="search"
            ></v-text-field> -->
            <v-btn v-if="imports.length"
               x-small
               class="mr-2 mt-1"
               color="gray darken-1"
               :disabled="btnRefreshImportsDisabled || !imports.length"
               @click="getImports()"
            >REFRESH{{importsDisabledDuration ? ' in ' + importsDisabledDuration + ' sec' : ''}}
               <v-icon right dark>refresh</v-icon>
            </v-btn>
            <v-btn v-if="$store.getters.user.policies.includes('contact-imports-create')"
               x-small
               class="mr-2 mt-1"
               color="gray darken-1"
               :disabled="loadingImports"
               @click="newImportClicked()"
            >NEW IMPORT
               <v-icon right dark>add</v-icon>
            </v-btn>
         </div>
      </v-card-title>
      <!-- <v-card-text v-if="loadingConfig" class="v-data-table__empty-wrapper">Loading account configuration. Please wait...</v-card-text> -->
      <v-card-text class="py-0">
         <bt-filter-wrapper-with-panel
            :closed-on-load="true"
            :fields="searchFields"
            :included-tabs="['standard']"
            :is-admin="jwt.pa"
            :max="searchFields.length"
            :preselected-fields="[]"
            :should-init="shouldInitFilterDefiner2"
            :std-field-values="searchFieldsValues"
            v-model="filter2"
            @filter-change="filterChanged2"
         ></bt-filter-wrapper-with-panel>
      </v-card-text>
      <v-card-text>
         <!-- :hide-default-footer="imports.length < 11" -->
         <v-data-table dense fixed-header show-expand
            class="elevation-1"
            item-key="name"
            :footer-props="{
               itemsPerPageOptions: [5, 10, 20],
               showFirstLastPage: true
            }"
            :headers="headers"
            :hide-default-footer="importsCount <= 5"
            :items="imports"
            :items-per-page="5"
            :loading="loadingImports"
            :loading-text="$t('loading-text')"
            :no-data-text="$t('no-data-text', { value: 'imports' })"
            :no-results-text="$t('no-results-text', { value: 'imports' })"
            :options.sync="options"
            :search="search"
            :server-items-length="importsCount"
            :single-expand="false"
         >
            <template v-slot:[`item.creationDate`]="{ item }">
               {{ formatDate(item.creationDate, true) }}
            </template>
            <template v-slot:[`item.status`]="{ item }">
               <v-icon :color="getIcon(item.status).color">{{getIcon(item.status).icon}}</v-icon><span :text--color="getIcon(item.status).color"> {{ item.status }}</span>
               <!-- <v-icon v-if="item.status==='processing'" color="blue">hourglass_empty</v-icon> {{ item.status }} -->
            </template>
            <template v-slot:[`item.processedRecords`]="{ item }">
               <v-tooltip v-if="item.processedRecords"
                  top color="black"
               >
                  <template v-slot:activator="{ on, attrs }">
                     <span v-bind="attrs" v-on="on">{{item.processedRecords}}</span>
                  </template>
                  <span>Processing Time: {{calculateProcessingTime(item.lastActivityDate, item.startDate, 0)}}</span>
                  <br>
                  <span>Processing Speed: {{calculateProcessingSpeed(item.processedRecords, item.lastActivityDate, item.startDate, 0)}} Rec/Hr</span>
               </v-tooltip>
               <span v-else>{{item.hasOwnProperty('processedRecords') ? new Intl.NumberFormat().format(item.processedRecords) : ''}}</span>
            </template>
            <template v-slot:[`item.startDate`]="{ item }">
               {{ formatDate(item.startDate, true) }}
            </template>
            <template v-slot:[`item.lastActivityDate`]="{ item }">
               {{ formatDate(item.lastActivityDate, true) }}
            </template>
            <template v-slot:[`item.purlStatus`]="{ item }">
               <v-icon :color="getIcon(item.purlStatus).color">{{getIcon(item.purlStatus).icon}}</v-icon><span :text--color="getIcon(item.purlStatus).color"> {{ item.purlStatus }}</span>
            </template>
            <template v-slot:[`item.purlProcessedRecords`]="{ item }">
               <v-tooltip v-if="item.purlProcessedRecords"
                  top color="black"
               >
                  <template v-slot:activator="{ on, attrs }">
                     <span v-bind="attrs" v-on="on">{{item.purlProcessedRecords}}</span>
                  </template>
                  <span>Processing Time: {{calculateProcessingTime(item.purlLastActivityDate, item.purlStartDate, 0)}}</span>
                  <br>
                  <span>Processing Speed: {{calculateProcessingSpeed(item.purlProcessedRecords, item.purlLastActivityDate, item.purlStartDate, 0)}} Rec/Hr</span>
               </v-tooltip>
               <span v-else>{{item.hasOwnProperty('purlPocessedRecords') ? new Intl.NumberFormat().format(item.purlProcessedRecords) : ''}}</span>
            </template>
            <template v-slot:[`item.purlLastActivityDate`]="{ item }">
               {{ formatDate(item.purlLastActivityDate, true) }}
            </template>

            <!-- <template v-slot:[`item.action`]="{ item }">
               <v-icon small
                  :disabled="item.status !='waiting' && item.status !='recovery'"
                  @click="updateImport(item)"
               >cancel</v-icon>
            </template> -->

            <template v-slot:expanded-item="{ item }">
               <td colspan="6" class="py-2" valign="top" dense>
                  <ul>
                     <li>
                        <span class="expanded-header">PURLs: </span>
                        <span class="expanded-content">{{item.generatePURL ? item.purlColumns.join(', ') : ''}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Dedups: </span>
                        <span class="expanded-content">{{item.generatePURL ? item.dedupColumns.join(', ') : ''}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Notification Emails: </span>
                        <span class="expanded-content">{{item.notificationEmails ? item.notificationEmails.join(', ') : ''}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Latitude Column: </span>
                        <span class="expanded-content">{{item.latitudeColumn}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Longitude Column: </span>
                        <span class="expanded-content">{{item.longitudeColumn}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Generate PURL: </span>
                        <span class="expanded-content text-capitalize">{{item.generatePURL}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">PURL Generation Scope: </span>
                        <span class="expanded-content text-capitalize">{{item.purlGenerationScope}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Same PURL for the same Contact: </span>
                        <span class="expanded-content text-capitalize">{{item.dedupHashMatching}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Auto Export: </span>
                        <span class="expanded-content text-capitalize">{{item.exportOnImportCompletion}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Cast Types: </span>
                        <span class="expanded-content text-capitalize">{{item.typeCasting || false}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Batch Size: </span>
                        <span class="expanded-content">{{item.processBatchSize}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">File Delimiter: </span>
                        <span class="expanded-content">{{getDelimiterDesc(item.fileDelimiter)}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">File Path: </span>
                        <span class="expanded-content text-capitalize">{{item.filePath}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">DB Name: </span>
                        <span class="expanded-content text-capitalize">{{item.databaseName}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Recovery Count: </span>
                        <span class="expanded-content">{{item.recoveryCount}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">Recovery Error: </span>
                        <span class="expanded-content">{{item.recoveryError}}</span>
                     </li>
                     <li>
                        <span class="expanded-header">ID: </span>
                        <span class="expanded-content text-capitalize">{{item._id}}</span>
                     </li>
                  </ul>
               </td>
               <td colspan="5" class="py-2" valign="top" dense>
                  <ul>
                     <li>
                        <span class="expanded-header">Header({{item.header ? item.header.length : 0}}): </span>
                        <span class="expanded-content">{{item.header ? item.header.join(', ') : ''}}</span>
                     </li>
                  </ul>
               </td>
            </template>
         </v-data-table>
      </v-card-text>
      <!-- <v-card-text v-if="!configData._id && !loadingConfig">You need to configure your account first.</v-card-text> -->
   </v-card>

   <v-form ref="mainForm" v-model="isMainFormValid" lazy-validation>
      <v-dialog v-model="dialogImport" persistent no-click-animation max-width="1080px">
         <v-card flat>
            <v-card-title class="title grey--text darken-4 font-weight-bold pb-2">Creating New Import:</v-card-title>
            <v-card-text :loading="loadingNewImport">
               <v-tabs dark
                  class="elevation-2"
                  background-color="grey lighten-1 accent-4"
                  v-model="tab"
               >
                  <v-tab 
                     :class="isMainFormValid ? '' : 'red--text'"
                  >Main Info</v-tab>
                  <v-tab v-if="formData.shouldGeneratePurl"
                     :class="isPurlsFormValid ? '' : 'red--text'"
                  >PURL Columns</v-tab>
                  <v-tab v-if="formData.shouldGeneratePurl"
                     :class="isDedupsFormValid ? '' : 'red--text'"
                  >Dedup Columns</v-tab>
                  <v-tab disabled
                     :class="isEmailsFormValid ? '' : 'red--text'"
                  >Notification Emails</v-tab>
                  <v-tab disabled
                     :class="isPgpFormValid ? '' : 'red--text'"
                  >SFTP and PGP</v-tab>

                  <v-tabs-items v-model="tab">
                     <v-tab-item>
                        <v-card flat tile>
                           <v-card-text>
                              <!-- <v-form ref="mainForm" v-model="isMainFormValid" lazy-validation> -->
                                 <v-row>
                                    <v-col class="py-0 font-weight-bold">Please note that "Generate PURL" and "Same PURL for the same Contact" options cannot be changed after the 1st import is submitted.</v-col>
                                 </v-row>
                                 <v-row>
                                    <v-col xs="12" sm="12" md="4" class="py-0">
                                       <v-switch
                                          class="mx-0 mt-2 mb-0 pb-0"
                                          label="Generate PURL"
                                          :disabled="hasImport"
                                          v-model="formData.shouldGeneratePurl"
                                          @change="generatePurlChanged"
                                       ></v-switch>
                                    </v-col>
                                    <v-col xs="12" sm="12" md="4" class="py-0">
                                       <v-switch
                                          class="mx-0 mt-2 mb-0 pb-0"
                                          label="Same PURL for the same contact"
                                          :disabled="!formData.shouldGeneratePurl || hasImport"
                                          v-model="formData.dedupHashMatching"
                                       ></v-switch>
                                    </v-col>
                                    <v-col xs="12" sm="12" md="2" class="py-0">
                                       <v-switch
                                          class="mx-0 mt-2 mb-0 pb-0"
                                          v-model="formData.shouldExport"
                                          label="Auto Export"
                                       ></v-switch>
                                    </v-col>
                                    <v-col xs="12" sm="12" md="2" class="py-0">
                                       <v-switch
                                          class="mx-0 mt-2 mb-0 pb-0"
                                          v-model="formData.shouldCastTypes"
                                          label="Cast Types"
                                       ></v-switch>
                                    </v-col>
                                 </v-row>
                                 <v-row>
                                    <v-col xs="12" sm="12" md="4" class="py-0">
                                       <v-text-field autofocus dense persistent-hint required
                                          v-model="formData.importName"
                                          ref="importName"
                                          hint="* Import Name"
                                          :counter="100"
                                          :maxlength="100"
                                          :placeholder="`${hasError('importName') ? 'import name' : ''}`"
                                          :rules="[rules.required, rules.minLength, rules.duplicate]"
                                       ></v-text-field>
                                    </v-col>
                                    <v-col xs="12" sm="12" md="4" class="py-0">
                                       <v-text-field clearable dense persistent-hint
                                          v-model="formData.latitudeColumn"
                                          hint="Latitude Column"
                                       ></v-text-field>
                                    </v-col>
                                    <v-col xs="12" sm="12" md="4" class="py-0">
                                       <v-text-field clearable dense persistent-hint
                                          v-model="formData.longitudeColumn"
                                          hint="Longitude Column"
                                       ></v-text-field>
                                    </v-col>
                                 </v-row>
                                 <v-row>
                                    <v-col xs="12" sm="12" md="4" class="py-2">
                                       <v-select dense persistent-hint hide-selected required
                                          ref="batchSize"
                                          v-model="formData.batchSize"
                                          hint="* Import Batch Size"
                                          :items="batchSizeItems"
                                       ></v-select>
                                    </v-col>
                                    <v-col xs="12" sm="12" md="4" class="py-2">
                                       <v-select dense persistent-hint hide-selected required
                                          ref="delimiter"
                                          v-model="formData.delimiter"
                                          hint="* File Delimiter"
                                          :items="delimiterItems"
                                       ></v-select>
                                    </v-col>
                                    <v-col xs="12" sm="12" md="4" class="py-2">
                                       <v-select dense persistent-hint hide-selected required
                                          ref="scope"
                                          v-model="formData.scope"
                                          :hint="`* PURL Generation Scope${formData.scope==='account' ? '' : ': Cannot be changed anymore'}`"
                                          :disabled="!canModifyScope"
                                          :items="scopeItems"
                                       ></v-select>
                                    </v-col>
                                 </v-row>
                              <!-- </v-form> -->
                           </v-card-text>
                        </v-card>
                     </v-tab-item>

                     <v-tab-item v-if="formData.shouldGeneratePurl">
                        <v-card flat tile>
                           <v-card-text class="py-2 px-0">
                              <bt-dynamic-text-fields
                                 field-label=""
                                 field-placeholder="purl column"
                                 field-type="string"
                                 :debug="debug"
                                 :field-dense="true"
                                 :field-disabled="imports.length > 0 && imports[0].purlGenerationScope === 'account' && imports[0].purlColumns && imports[0].purlColumns.length > 0 && formData.scope === 'account'"
                                 :field-max="3"
                                 :field-outlined="false"
                                 :field-required="true"
                                 :should-init="formData.shouldInitPurls"
                                 v-model="formData.purls"
                                 @form-validation="purlsFormValidated"
                              ></bt-dynamic-text-fields>
                           </v-card-text>
                        </v-card>
                     </v-tab-item>

                     <v-tab-item v-if="formData.shouldGeneratePurl">
                        <v-card flat tile>
                           <v-card-text class="py-2 px-0">
                              <bt-dynamic-text-fields
                                 field-placeholder="dedup column"
                                 field-type="string"
                                 field-validation=""
                                 :debug="debug"
                                 :field-dense="true"
                                 :field-disabled="imports.length > 0 && imports[0].purlGenerationScope === 'account' && imports[0].dedupColumns && imports[0].dedupColumns.length > 0 && formData.scope === 'account'"
                                 :field-max="5"
                                 :field-required="true"
                                 :should-init="formData.shouldInitDedups"
                                 v-model="formData.dedups"
                                 @form-validation="dedupsValidated"
                              ></bt-dynamic-text-fields>
                           </v-card-text>
                        </v-card>
                     </v-tab-item>

                     <v-tab-item>
                        <v-card flat tile>
                           <v-card-text class="py-2 px-0">
                              <bt-dynamic-text-fields
                                 field-placeholder="email"
                                 field-type="email"
                                 :debug="debug"
                                 :field-dense="true"
                                 :field-required="false"
                                 :should-init="formData.shouldInitEmails"
                                 v-model="formData.emails"
                                 @form-validation="emailsValidated"
                              ></bt-dynamic-text-fields>
                           </v-card-text>
                        </v-card>
                     </v-tab-item>

                     <v-tab-item>
                        <v-card flat tile>
                           <v-card-text class="py-2 px-0">
                              <v-row>
                                 <v-col xs="12" sm="12" md="4" class="py-0">
                                       :rules="[rules.required, rules.minLength, rules.duplicate]"
                                    <v-text-field autofocus clearable dense persistent-hint
                                       ref="pgpName"
                                       hint="Name"
                                       :counter="100"
                                       :maxlength="100"
                                       :placeholder="`${hasError('keyName') ? 'key name' : ''}`"
                                       v-model="formData.keyName"
                                    ></v-text-field>
                                 </v-col>
                                 <v-col xs="12" sm="12" md="4" class="py-0">
                                    <v-text-field v-if="formData.keyName"
                                       clearable dense persistent-hint
                                       hint="Email"
                                       v-model="formData.pgpEmail"
                                    ></v-text-field>
                                 </v-col>
                                 <v-col xs="12" sm="12" md="4" class="py-0">
                                    <v-text-field v-if="formData.keyName"
                                       clearable dense persistent-hint
                                       hint="Passphrase"
                                       :type="passphraseType"
                                       v-model="formData.pgpPassphrase"
                                    ></v-text-field>
                                 </v-col>
                              </v-row>
                           </v-card-text>
                        </v-card>
                     </v-tab-item>
                  </v-tabs-items>
               </v-tabs>
            </v-card-text>

            <v-card-actions>
               <div class="flex-grow-1"></div>
               <v-btn text
                  color="blue darken-1"
                  @click="cancelImport"
               >Cancel</v-btn>
               <v-btn v-if="!isActualEndpoint"
                  text
                  color="blue darken-1"
                  :disabled="!isImportFormValid"
                  @click="saveImport"
               >Save</v-btn>
               <import-file-upload @uploaded="saveImport" :notActive="!isImportFormValid" />
            </v-card-actions>
         </v-card>
      </v-dialog>
   </v-form>

</v-container>
</template>

<script>
import BtFilterWrapperWithPanel from './BtFilterWrapperWithPanel.vue';
import BtDynamicTextFields from './BtDynamicTextFields.vue';
import { APIService } from '../services/cs-api-service.js';
import { addSeconds, differenceInHours, differenceInMinutes, differenceInSeconds, format, parseISO } from "date-fns";
import ImportFileUpload from './ImportFileUpload.vue';

class FormData {
   constructor(initData, shouldInit) {
      this.scope = initData.purlGenerationScope || 'account';
      this.purls = initData.purlColumns || [];
      this.shouldInitPurls = shouldInit;

      this.dedups = initData.dedupColumns || [];
      this.shouldInitDedups = shouldInit;

      this.emails = initData.notificationEmails || [];
      this.shouldInitEmails = shouldInit;

      this.validations = initData.columnValidation || [];
      this.shouldInitValidations = shouldInit;

      this.indexes = initData.indexObject || {};
      this.shouldInitIndexes = shouldInit;

      this.mappings = initData.columnMapping || {};
      this.shouldInitMappings = shouldInit;

      this.latitudeColumn = initData.latitudeColumn || '';
      this.longitudeColumn = initData.longitudeColumn || '';
      this.delimiter = initData.fileDelimiter || ',';
      this.batchSize = initData.processBatchSize || 100;
      this.shouldGeneratePurl = initData.hasOwnProperty('generatePURL') ? initData.generatePURL : true;
      this.shouldExport = initData.hasOwnProperty('exportOnImportCompletion') ? initData.exportOnImportCompletion : false;
      this.shouldCastTypes = initData.hasOwnProperty('typeCasting') ? initData.typeCasting : false;
      this.importName = '';
      this.dedupHashMatching = initData.hasOwnProperty('dedupHashMatching') ? initData.dedupHashMatching : false;

      this.pgpName = initData.pgpName || '';
      this.pgpEmail = initData.pgpEmail || '';
      this.pgpPassphrase = initData.pgpPassphrase || '';
   }
}

export default {
   name: "CsImport",

   components: {
      BtFilterWrapperWithPanel,
      BtDynamicTextFields,
      ImportFileUpload
   },

   props: {
      debug: {
         type: Boolean,
         default: false
      },
      isActualEndpoint: {
         type: Boolean,
         default: true
      }
   },

   data() {
      return {
         jwt: {},
         apiService: null,
         tab: null,
         formData: new FormData({}, false),
         imports: [],
         rules: {
            required: value => !!value || "Value is required!",
            minLength: value => value.length >= 5 || "Name should have more than 5 charecters",
            duplicate: value => this.imports.filter(imprt => imprt.name === value).length === 0 || 'Value is duplicate!'
         },
         headers: [
            { text: 'Name', value: 'name', align: 'left', sortable: true },
            { text: 'Creation', value: 'creationDate', align: 'left', sortable: false },
            { text: 'Status', value: 'status', align: 'left', sortable: true },
            { text: 'Processed Records', value: 'processedRecords', align: 'left', sortable: true },
            { text: 'Start', value: 'startDate', align: 'left', sortable: true },
            { text: 'Last Activity', value: 'lastActivityDate', align: 'left', sortable: true },
            { text: 'PURL Status', value: 'purlStatus', align: 'left', sortable: true },
            { text: 'PURL Processed Records', value: 'purlProcessedRecords', align: 'left', sortable: true },
            { text: 'PURL Last Activity', value: 'purlLastActivityDate', align: 'left', sortable: true }
            // { text: '', value: 'action', align: 'right', sortable: false }
         ],
         batchSizeItems: [
            { text: '25', value: 25 },
            { text: '50', value: 50 },
            { text: '75', value: 75 },
            { text: '100', value: 100 }
         ],
         delimiterItems: [
            { text: 'Comma', value: ',' },
            { text: 'Tab', value: '\t' },
            { text: 'Pipe', value: '|' }
         ],
         scopeItems: [
            { text: 'Account Level', value: 'account' },
            { text: 'Import Level', value: 'import' }
         ],
         search: '',
         tdHeader: 'font-weight-bold font-italic',
         tdContent: 'font-italic',
         importsDisabledDuration: 0,
         btnRefreshImportsDisabled: true,
         loadingImports: false,
         loadingNewImport: false,
         dialogImport: false,
         dialogUpload: false,
         isMainFormValid: false,
         isPurlsFormValid: false,
         isDedupsFormValid: false,
         isEmailsFormValid: true,
         isPgpFormValid: true,
         canModifyScope: false,
         importsCount: 0,
         options: {},
         shouldInitFilterDefiner2: false,
         filter2: {
            standard: [{ $match: {} }],
            // columns: []
         },
         searchFields: [
            { text: 'Name', value: 'name', type: 'string', isIndexed: true },
            { text: 'Creation Date', value: 'creationDate', type: 'date', isIndexed: true },
            { text: 'Status', value: 'status', type: 'string', isIndexed: true },
            { text: 'Processed Records', value: 'processedRecords', type: 'number', isIndexed: true },
            { text: 'Start Date', value: 'startDate', type: 'date', isIndexed: true },
            { text: 'Last Activity Date', value: 'lastActivityDate', type: 'date', isIndexed: true },
            { text: 'PURL Status', value: 'purlStatus', type: 'string', isIndexed: true },
            { text: 'PURL Processed Records', value: 'purlProcessedRecords', type: 'number', isIndexed: true },
            { text: 'PURL Last Activity Date', value: 'purlLastActivityDate', type: 'date', isIndexed: true }
         ],
         searchFieldsValues: {
            status: ['waiting','processing','completed','recovery','rejected'],
            purlStatus: ['waiting','processing','completed','recovery','rejected']
         },
         hasImport: false
      }
   },

   computed: {
      token() {
         return this.$store.getters.token;
      },

      isImportFormValid() {
         return this.formData.importName && this.isMainFormValid && this.isEmailsFormValid &&
            (this.formData.shouldGeneratePurl ?
               this.isPurlsFormValid && this.isDedupsFormValid : true);
      }
   },

   watch: {
      token() {
         // alert('in watch for token');
         this.init();
         this.nextAction();
      },

      options: {
         handler (val) {
            // alert('in watch: options=' + JSON.stringify(val));
            if (val.sortBy.length > 0) {
               const sort = {};
               sort[val.sortBy[0]] = val.sortDesc[0] ? -1 : 1;
               this.filter2.sort = sort;
            }
            this.getImports();
         }
      }
   },

   methods: {
      log(msg) {
         if (this.debug)
            console.log(`-----CsImport B1.4 says => ${msg}`);
      },

      logout() {
         this.$router.push('/');
      },

      hasError(ref) {
         let res = false;
         if (!this.isMainFormValid && this.$refs[ref]) {
            // if (ref === 'importName') alert('isDirty=' + this.$refs[ref].isDirty);
            res = this.$refs[ref].hasError;
         }

         return res;
      },

      async init() {
         try {
            // alert('in init(): token=' + this.token);
            this.imports = [];

            if (this.token) {
               const JWT = JSON.parse(atob(this.token.split('.')[1]));
               this.jwt = {
                  aid: JWT.aid,
                  paid: JWT.paid,
                  pa: JWT.pa,
                  pu: JWT.pu
               };
               this.log('in init(): jwt=' + JSON.stringify(this.jwt));
               this.apiService = new APIService(this.jwt, this.token, this.debug, this.isActualEndpoint);
               this.importsCount = await this.getImportsCount(this.filter2);
               this.hasImport = this.importsCount > 0 ? true : false;
               this.shouldInitFilterDefiner2 = true;
            } else
               this.jwt = {};
         } catch (error) {
            alert('Exception while parsing token: ' + error.message);
         }
      },

      async getImportsCount() {
         this.loadingImports = true;
         let result = await this.apiService.getImportsCount(this.filter2);
         this.loadingImports = false;
         if (result.logout)
            this.logout();

         return result.message ? 0 : result.data;
      },

      async getImports() {
         // alert(`in getImports(): filter2=${JSON.stringify(this.filter2)}`);
         this.loadingImports = true;
         this.btnRefreshImportsDisabled = true;
         this.imports = [];
         let result = await this.apiService.getImports(this.filter2, this.options.itemsPerPage, this.options.page);
         if (result.logout)
            this.logout();
            
         if (!result.message) {
            this.imports = result.data;
         }

         if (this.imports.length === 0 || this.imports[0].purlGenerationScope === 'account')
            this.canModifyScope = true;
         else
            this.canModifyScope = false;

         if (this.imports.length || result.message) {
            this.importsDisabledDuration = 10;
            setTimeout(() => {
               this.btnRefreshImportsDisabled = false;
            }, 1000 * 10);

            const setIntervalImports = setInterval(() => {
               if (this.importsDisabledDuration > 0)
                  this.importsDisabledDuration--;
               else
                  clearInterval(setIntervalImports);
            }, 1000);
         }

         this.loadingImports = false;
      },

      getIcon(status) {
         switch (status) {
            case 'waiting':
               return { color: 'grey', icon: 'schedule' };
            case 'processing':
               return { color: 'blue', icon: 'hourglass_empty' };
            case 'completed':
               return { color: 'green', icon: 'done' };
            case 'recovery':
               return { color: 'orange', icon: 'restore' };
            case 'rejected':
               return { color: 'red', icon: 'clear' };
            default:
               return { color: '', icon: '' };
         }
      },

      async updateImport(importData) {
         if (confirm(`Are you sure you want to cancel '${importData.name}'?`)) {
            this.log('in updateImport(): importData=' + JSON.stringify(importData));
            this.loadingImports = true;
            const result = await this.apiService.updateImport(importData);
            if (result.logout)
               this.logout();
            else if (!result.message) {
               // importData.status = result.data.status;
               // importData.recoveryError = result.data.recoveryError;
               // importData.lastActivityDate = result.data.lastActivityDate;
               importData = result.data;
               this.$emit('snackbar-event', `'${importData.name}' import was cancelled.`);
            }
            this.loadingImports = false;
         }
      },

      getDelimiterDesc(delimiter) {
         return this.delimiterItems.filter(d => d.value === delimiter)[0].text;
      },

      formatDate(date, withTime) {
         if (date) {
            // alert('in formatDate(): date='+date+'\nwithTime=' + withTime + '\nparseISO='+parseISO(date));
            const formatteddate = format(parseISO(date), 'M/d/yyyy h:mm:ss a');
            // const dateParts = formatteddate.split(' ');
            // if (withTime) return dateParts[0] + '\n' + dateParts[1] + ' ' + dateParts[2];
            if (withTime) return formatteddate;
            else return formatteddate.split(' ')[0];
         }
      },

      calculateProcessingTime(dateEnded, dateStarted, extraSeconds) {
         const isoEnded = parseISO(dateEnded);
         const isoStarted = addSeconds(parseISO(dateStarted), extraSeconds || 0);
         const h = differenceInHours(isoEnded, isoStarted);
         const min = differenceInMinutes(isoEnded, isoStarted);
         const m = min - h * 60;
         const s = differenceInSeconds(isoEnded, isoStarted) - h * 3600 - m * 60;
         let diff = '';
         if (h) diff = h + 'h ';
         if (min) diff += m + 'm ';
         diff += s + 's';
         return diff;
      },

      calculateProcessingSpeed(processedRecs, dateEnded, dateStarted, extraSeconds) {
         const cntPerHour = new Intl.NumberFormat().format(Math.round(processedRecs * 3600 / differenceInSeconds(parseISO(dateEnded), addSeconds(parseISO(dateStarted), extraSeconds || 0))));
         // alert(`processedRecs=${processedRecs}\ndateEnded=${dateEnded}\ndateStarted=${dateStarted}\ncntPerHour=${cntPerHour}`);
         return cntPerHour;
      },

      getMappings(columnMapping) {
         const mappings = [];
         if (columnMapping)
            for (let prop in columnMapping) {
               mappings.push(prop + '=' + columnMapping[prop]);
            }
         return mappings;
      },

      async newImportClicked() {
         this.dialogImport = true;
         // this.formData.shouldValidatePurls = true;
         // this.formData.shouldValidateDedups = true;
         // this.formData = new FormData(this.imports.length > 0 ? this.imports[0] : {}, true);
         this.formData = new FormData(this.imports[0] || {}, true);
         setTimeout(() => {
            this.$refs.importName.focus();
         }, 10);

         // alert('in newImportClicked(): formData=' + JSON.stringify(this.formData));
      },

      purlsFormValidated(val) {
         this.log('in purlsFormValidated(): val=' + val);
         this.isPurlsFormValid = val;
      },
      dedupsValidated(val) {
         this.log('in dedupsValidated(): val=' + val);
         this.isDedupsFormValid = val;
      },
      emailsValidated(val) {
         this.log('in emailsValidated(): val=' + val);
         this.isEmailsFormValid = val;
      },

      generatePurlChanged(val) {
         if (!val) {
            this.isPurlsFormValid = true;
            this.formData.dedupHashMatching = false;
         }
      },

      cancelImport() {
         this.dialogImport = false;
         this.formData = new FormData({}, false);
         this.tab = 0;
      },

      async saveImport(arg) {
         // this.formData.shouldValidatePurls = true;
         // this.formData.shouldValidateDedups = true;
         // this.formData.shouldValidateMappings = true;
         this.isMainFormValid = this.$refs.mainForm.validate();

         // alert('isPurlsFormValid=' + this.isPurlsFormValid +
         //       '\nisDedupsFormValid=' + this.isDedupsFormValid  +
         //       '\nisEmailsFormValid=' + this.isEmailsFormValid  +
         //       '\nisMainFormValid=' + this.isMainFormValid);

         if (this.isImportFormValid) {
            // alert('in saveImport(): formData.mappings=' + JSON.stringify(this.formData));
            this.loadingNewImport = true;

            const importData = {
               exportOnImportCompletion: this.formData.shouldExport,
               fileDelimiter: this.formData.delimiter,
               generatePURL: this.formData.shouldGeneratePurl,
               name: this.formData.importName,
               processBatchSize: this.formData.batchSize,
               purlGenerationScope: this.formData.scope,
               dedupHashMatching: this.formData.dedupHashMatching,
               typeCasting: this.formData.shouldCastTypes
            };

            if (this.formData.dedups.length > 0)
               importData.dedupColumns = this.formData.dedups;
            if (this.formData.latitudeColumn.trim())
               importData.latitudeColumn = this.formData.latitudeColumn;
            if (this.formData.longitudeColumn.trim())
               importData.longitudeColumn = this.formData.longitudeColumn;
            if (this.formData.emails.length > 0)
               importData.notificationEmails = this.formData.emails;
            if (this.formData.purls.length > 0)
               importData.purlColumns = this.formData.purls;
            if (arg.hasOwnProperty('fileName'))
               importData.filePath = arg.fileName;

            this.log('in saveImport(): importData=' + JSON.stringify(importData));
            const result = await this.apiService.createImport(importData);
            this.loadingNewImport = false;
            if (result.logout)
               this.logout();
            else if (!result.message) {
               this.$emit('snackbar-event', `'${importData.name}' was saved.`);
               // this.importsCount = await this.getImportsCount();
               this.importsCount++;
               this.getImports();
               this.cancelImport();
               this.hasImport = true;
            }
         }
      },

      async filterChanged2(filter) {
         // alert('in filterChanged2(): filter=' + JSON.stringify(filter) + '\noptions=' + JSON.stringify(this.options));
         this.filter2 = filter;
         this.importsCount = await this.getImportsCount();
         this.nextAction();
      },

      async nextAction() {
         const currOptions = JSON.stringify(this.options);
         const newOptions = JSON.parse(currOptions);
         newOptions.page = 1;
         if (JSON.stringify(newOptions) === currOptions)
            await this.getImports();
         else
            this.options = newOptions;
      }
   },

   created() {      
      this.init();
   }
}
</script>

<style scoped>
.expanded-header {
   font-style: italic;
   font-weight: bold;
}
</style>
